import PropTypes from 'prop-types';
import { Box, Typography, Container, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from '@classity-edtech/ui-base';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import {
  CallIcon,
  ChevronDown as ChevronDownIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';

export interface PricingPlanAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'text';
  icon?: 'chevron' | 'call';
  href?: string;
  onClick?: () => void;
}

export interface PricingPlanItem {
  title: string;
  price?: string | number;
  period?: string;
  description: string | string[];
  actions: PricingPlanAction[];
  featured?: boolean;
}

export interface PricingPlanProps {
  className?: string;
  id?: string;
  heading?: string;
  subheading?: string;
  plans: PricingPlanItem[];
}

const PricingCard = ({ plan, isMobile }: { plan: PricingPlanItem; isMobile: boolean }) => {
  const scrollToSection = (href?: string) => {
    if (!href || typeof document === 'undefined') {
      return;
    }

    const targetId = href.startsWith('#') ? href.slice(1) : href.split('#')[1];

    if (!targetId) {
      return;
    }

    const section = document.getElementById(targetId);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleActionClick = (action?: PricingPlanAction) => (event?: any) => {
    if (!action) {
      return;
    }

    if (action.href && (action.href.startsWith('#') || action.href.includes('#'))) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      scrollToSection(action.href);
      return;
    }

    action.onClick?.();
  };

  return (
    <Box
      sx={{
        border: plan.featured
          ? 'none'
          : `1px solid ${globalColours['$global-colour-border-neutral']}`,
        backgroundColor: plan.featured
          ? globalColours['$global-colour-palette-orange-900']
          : globalColours['$global-colour-background-elevated'],
        borderRadius: '16px',
        p: 5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: plan.featured ? '339px' : 'auto',
        minHeight: plan.featured ? '339px' : '339px',
        overflow: 'hidden',
        flex: { xs: 'initial', md: plan.featured ? '0 0 calc(2 * 360px + 32px)' : '1 1 0' },
        width: {
          xs: '100%',
          md: plan.featured ? 'calc(2 * 360px + 32px)' : '360px',
        },
      }}
    >
      {/* Heading */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: plan.price ? 1.5 : 1,
          width: '100%',
        }}
      >
        <Typography
          variant='subtitle4'
          sx={{
            color: plan.featured
              ? globalColours['$global-colour-base-contrast']
              : globalColours['$global-colour-content-primary'],
          }}
        >
          {plan.title}
        </Typography>

        {/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(plan.price as string) ? (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              variant='subtitle1'
              sx={{
                color: plan.featured
                  ? globalColours['$global-colour-base-contrast']
                  : globalColours['$global-colour-content-primary'],
              }}
            >
              {`₦${plan.price}`}
            </Typography>
            {plan.period && (
              <Typography
                variant='body2SemiBold'
                sx={{
                  color: plan.featured
                    ? globalColours['$global-colour-base-contrast']
                    : globalColours['$global-colour-content-primary'],
                }}
              >
                {`/${plan.period}`}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography
            variant='subtitle1'
            sx={{
              color: plan.featured
                ? globalColours['$global-colour-base-contrast']
                : globalColours['$global-colour-content-primary'],
            }}
          >
            {plan.price}
          </Typography>
        )}
      </Box>

      {/* Description */}
      <Box sx={{ width: '100%' }}>
        {Array.isArray(plan.description) ? (
          <Box>
            {plan.description.map((line, index) => (
              <Typography
                key={index}
                variant='body2SemiBold'
                sx={{
                  color: plan.featured
                    ? globalColours['$global-colour-base-contrast']
                    : globalColours['$global-colour-content-secondary'],
                  mb: index < plan.description.length - 1 ? 0 : 0,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography
            variant='body2SemiBold'
            sx={{
              color: plan.featured
                ? globalColours['$global-colour-base-contrast']
                : globalColours['$global-colour-content-secondary'],
            }}
          >
            {plan.description}
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Stack
        direction={plan.featured ? { xs: 'column', md: 'row' } : 'row'}
        spacing={plan.featured ? 0 : 2}
        sx={{
          py: 1.25,
          width: '100%',
          flexWrap: 'nowrap',
          gap: { xs: 1.5, md: 2 },
          alignItems: plan.featured ? { xs: 'stretch', md: 'flex-start' } : 'flex-start',
        }}
      >
        {plan.actions.map((action, index) => {
          if (plan.featured) {
            return action?.variant === 'primary' ? (
              <Button
                key={index}
                onClick={handleActionClick(action)}
                disableBottomFixed
                sx={{
                  backgroundColor: '#D22F11',
                  color: '#FFFFFF',
                  px: 3,
                  py: 1.5,
                  position: 'relative',
                  borderRadius: '100px',
                  '&:hover': {
                    backgroundColor: '#B32710',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Gill Sans, sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    letterSpacing: '-0.2px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {action?.label}
                </Typography>
              </Button>
            ) : (
              <Button
                key={index}
                variant='outlined'
                onClick={handleActionClick(action)}
                sx={{
                  backgroundColor: '#FFFFFF',
                  color: '#D22F11',
                  border: '1px solid #D22F11',
                  px: 3,
                  py: 1.5,
                  position: 'relative',
                  borderRadius: '100px',
                  '&:hover': {
                    backgroundColor: '#FFF5F3',
                    border: '1px solid #D22F11',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Gill Sans, sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    letterSpacing: '-0.2px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {action?.label}
                </Typography>
              </Button>
            );
          }

          // Non-featured cards
          return (
            action && (
              <Box
                key={index}
                onClick={handleActionClick(action)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  pl: action?.icon === 'chevron' ? 0 : undefined,
                  pr: action?.icon === 'call' ? 1 : undefined,
                  py: 1,
                  height: '32px',
                  flex: action?.icon === 'chevron' ? '1 0 0' : undefined,
                  minWidth: action?.icon === 'chevron' ? 0 : undefined,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {action?.icon === 'chevron' && (
                  <Box
                    sx={{
                      width: '12px',
                      height: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ChevronDownIcon
                      sx={{
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Box>
                )}
                {action?.icon === 'call' && (
                  <Box
                    sx={{
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CallIcon
                      sx={{
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Box>
                )}
                <Typography
                  variant='body2'
                  sx={{
                    color: globalColours['$global-colour-content-primary'],
                    whiteSpace: 'nowrap',
                  }}
                >
                  {action?.label}
                </Typography>
              </Box>
            )
          );
        })}
      </Stack>
    </Box>
  );
};

export const PricingPlan = ({ className, heading, subheading, plans, id }: PricingPlanProps) => {
  const screens = useAppMediaQuery();
  const isMobile = screens.isMobile || screens.isTablet;

  const buildRows = (items: PricingPlanItem[]) => {
    if (isMobile) {
      return [items];
    }

    const rows: PricingPlanItem[][] = [];
    let index = 0;

    while (index < items.length) {
      const currentPlan = items[index];
      const row: PricingPlanItem[] = [currentPlan];
      index += 1;

      if (currentPlan.featured) {
        if (index < items.length && !items[index].featured) {
          row.push(items[index]);
          index += 1;
        }

        rows.push(row);
        continue;
      }

      const nextPlan = items[index];

      if (!nextPlan || nextPlan.featured) {
        rows.push(row);
        continue;
      }

      if (row.length < 3) {
        row.push(nextPlan);
        index += 1;
      }

      if (row.length < 3 && index < items.length && !items[index].featured) {
        row.push(items[index]);
        index += 1;
      }

      rows.push(row);
    }

    return rows;
  };

  const rows = buildRows(plans);

  return (
    <Box
      className={className}
      sx={{
        backgroundColor: globalColours['$global-colour-background-screen-default'],
        width: '100%',
        py: 4,
      }}
      id={id}
    >
      <Container sx={{ px: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
          }}
        >
          {/* Heading */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pb: 3,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                height: '120px',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                py: 2,
                borderRadius: '16px',
                flex: '1 0 0',
                minWidth: 0,
                textAlign: 'center',
              }}
            >
              <Typography
                variant='subtitle1'
                sx={{
                  color: globalColours['$global-colour-content-primary'],
                  width: 'min-content',
                  minWidth: '100%',
                  textAlign: 'center',
                }}
              >
                {heading || 'Flexible Pricing Plans'}
              </Typography>
              <Typography
                variant='body2SemiBold'
                sx={{
                  color: globalColours['$global-colour-content-secondary'],
                  whiteSpace: 'nowrap',
                }}
              >
                {subheading || 'Choose the perfect plan for your business needs'}
              </Typography>
            </Box>
          </Box>

          {/* Cards */}
          <Stack
            spacing={4}
            sx={{
              width: '100%',
            }}
          >
            {rows.map((row, rowIndex) => (
              <Stack
                key={`pricing-row-${rowIndex}`}
                data-testid='pricing-plan-row'
                direction={{ xs: 'column', md: 'row' }}
                spacing={4}
                sx={{
                  width: '100%',
                  minHeight: { xs: 'auto', md: '339px' },
                  alignItems: { xs: 'stretch', md: 'flex-start' },
                }}
              >
                {row.map((plan, index) => (
                  <PricingCard key={`${rowIndex}-${index}`} plan={plan} isMobile={isMobile} />
                ))}
              </Stack>
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

PricingPlan.propTypes = {
  className: PropTypes.string,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      period: PropTypes.string,
      description: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
      actions: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          variant: PropTypes.oneOf(['primary', 'secondary', 'text']),
          icon: PropTypes.oneOf(['chevron', 'call']),
          onClick: PropTypes.func,
        }),
      ),
      featured: PropTypes.bool,
    }),
  ),
};

PricingPlan.defaultProps = {
  className: null,
  heading: 'Flexible Pricing Plans',
  subheading: 'Choose the perfect plan for your business needs',
  plans: [],
  id: '',
};

export default styled<any>(PricingPlan)(({ theme }) => ``);
